(() => {
  if (customElements.get('product-bundle-form')) return;

  class ProductBundleForm extends HTMLElement {
    constructor() {
      super();
      this._boundCloseModal = this.closeModal.bind(this);
      this._boundHandleModalKeydown = this._handleModalKeydown.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.syncUrl = this.hasAttribute('data-sync-url');
      // Intelligems: only rewrite prices from the IG test price API on surfaces
      // flagged as the homepage buy box (data-ig-home). Elsewhere behaves unchanged.
      this._igHome = this.hasAttribute('data-ig-home');

      this.options = this.querySelectorAll('.js-bundle-option');
      this.addBtn = this.querySelector('.js-bundle-add-subscription');
      this.onetimeTrigger = this.querySelector('.js-bundle-open-onetime');
      this.onetimeTriggerText = this.onetimeTrigger?.childNodes[0] || null;
      this.travelCheckbox = this.querySelector('.js-travel-checkbox');
      this.travelAddon = this.querySelector('.product-bundle-form__addon');
      this.travelPriceEl = this.querySelector('.js-travel-price-diff');
      this.modal = this.querySelector('[id^="bundle-onetime-modal"]');

      this.options.forEach((opt) => {
        opt.addEventListener('click', this._onBundleOptionClick.bind(this));
      });
      this.addBtn?.addEventListener('click', this._onAddSubscriptionClick.bind(this));
      this.onetimeTrigger?.addEventListener('click', this._onOnetimeClick.bind(this));
      if (this.modal) {
        this.querySelectorAll('.js-bundle-onetime-close').forEach((btn) => {
          btn.addEventListener('click', this._boundCloseModal);
        });
        this.modal.addEventListener('keydown', this._boundHandleModalKeydown);
        this.modal.querySelectorAll('.js-bundle-modal-card').forEach((card) => {
          card.addEventListener('click', this._onModalCardClick.bind(this));
        });
        const buyOnceBtn = this.modal.querySelector('.js-bundle-modal-buy-onetime');
        buyOnceBtn?.addEventListener('click', this._onModalBuyOnetimeClick.bind(this));
        const subBtn = this.modal.querySelector('.js-bundle-modal-add-subscription');
        subBtn?.addEventListener('click', this._onModalAddSubscriptionClick.bind(this));
      }

      const first = this.querySelector('.js-bundle-option.selected') || this.querySelector('.js-bundle-option');
      if (first) {
        this._setSelected(first);
        this._accordion(first);
        this._switchProductImages(first);
        this._switchFeatureImage(first);
      }

      // Intelligems: re-apply IG test prices to the one-time link + travel add-on
      // once the IG price API is available (it loads asynchronously).
      this._applyIgWhenReady();
    }

    // Intelligems: format a dollar amount to match `money_without_trailing_zeros` (USD).
    _igFmt(value) {
      if (value == null || isNaN(value)) return null;
      const n = Number(value);
      return '$' + (Number.isInteger(n) ? String(n) : n.toFixed(2));
    }

    // Intelligems: return the IG test price for a variant, else null.
    _igPrice(variantId) {
      if (!variantId) return null;
      try {
        const api = window.igData && window.igData.price;
        if (!api || typeof api.getPriceByVariantId !== 'function') return null;
        return this._igFmt(api.getPriceByVariantId(String(variantId)));
      } catch (e) {
        return null;
      }
    }

    // Intelligems: poll briefly for the IG price API, then refresh the JS-rendered prices.
    // Travel add-on is refreshed on every surface (separate product); the one-time link
    // and modal footer are only IG-driven on the homepage buy box (data-ig-home).
    _applyIgWhenReady() {
      let tries = 0;
      const tick = () => {
        if (window.igData && window.igData.price) {
          const sel = this._getSelectedOption();
          this._updateOnetimeTrigger(sel);
          this._updateTravelAddon(sel);
          const selCard = this.modal && this.modal.querySelector('.js-bundle-modal-card.selected');
          if (selCard) this._updateModalFooter(selCard);
          return;
        }
        if (tries++ < 40) setTimeout(tick, 250);
      };
      tick();
    }

    disconnectedCallback() {
      if (this.modal) {
        this.querySelectorAll('.js-bundle-onetime-close').forEach((btn) => {
          btn.removeEventListener('click', this._boundCloseModal);
        });
        this.modal.removeEventListener('keydown', this._boundHandleModalKeydown);
      }
    }

    _getSectionsToRender() {
      const cartContent = document.getElementById('cart-drawer__content');
      if (!cartContent || !cartContent.dataset.id) return [];
      return [
        { id: 'cart-drawer__content', section: cartContent.dataset.id, selector: '.cart-drawer__content' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    _setSelected(optionEl) {
      this.options.forEach((o) => o.classList.remove('selected'));
      if (optionEl) optionEl.classList.add('selected');
      if (this.addBtn && optionEl) {
        this.addBtn.dataset.variantId = optionEl.dataset.variantId || '';
        this.addBtn.dataset.sellingPlanId = optionEl.dataset.sellingPlanId || '';
      }

      if (this.syncUrl) {
        const url = new URL(window.location);
        url.searchParams.set('variant', optionEl.dataset.variantId);
        window.history.replaceState(null, '', url);
      }

      this._updateOnetimeTrigger(optionEl);
      this._updateTravelAddon(optionEl);
    }

    _updateOnetimeTrigger(optionEl) {
      if (!this.onetimeTrigger || !this.onetimeTriggerText) return;

      const baseLabel = this.onetimeTrigger.dataset.baseLabel || '';
      const showDynamicPrice = this.onetimeTrigger.dataset.showDynamicPrice === 'true';
      // Intelligems: prefer the IG test price for the one-time variant on the homepage buy box.
      const igPrice = this._igHome ? this._igPrice(optionEl?.dataset.oneTimeVariantId) : null;
      const formattedPrice = igPrice || optionEl?.dataset.oneTimePriceFormatted?.trim();
      this.onetimeTriggerText.textContent = showDynamicPrice && formattedPrice ? `${baseLabel} ${formattedPrice}` : baseLabel;
    }

    _updateTravelAddon(optionEl) {
      if (!this.travelAddon) return;
      const hasTravelAddon = this.travelAddon.dataset.travelAddonAvailable === 'true';
      this.travelAddon.classList.toggle('product-bundle-form__addon--hidden', !hasTravelAddon);
      if (this.travelCheckbox && !hasTravelAddon) {
        this.travelCheckbox.checked = false;
      }
      if (this.travelPriceEl) {
        // Intelligems: prefer the IG test price for the travel add-on variant (all surfaces —
        // the travel set is a separate product). The "+" prefix is static in the markup, so
        // only the inner .js-travel-price-diff span (which carries igPrice) is rewritten.
        const travelIgPrice = this._igPrice(this.travelCheckbox?.dataset.variantId);
        const travelDisplay = travelIgPrice || (this.travelAddon.dataset.travelAddonPrice || '');
        this.travelPriceEl.textContent = hasTravelAddon ? travelDisplay : '';
      }
    }

    _getSelectedOption() {
      return this.querySelector('.js-bundle-option.selected') || this.querySelector('.js-bundle-option');
    }

    addToCart(variantId, sellingPlanId) {
      if (this._adding) return Promise.reject();
      this._adding = true;
      const sections = this._getSectionsToRender();
      const body = { id: variantId, quantity: 1, sections: sections.map((s) => s.section) };
      if (sellingPlanId) body.selling_plan = sellingPlanId;

      return fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.description || data.message) {
            return Promise.reject(new Error(data.description || data.message));
          }
          return data;
        })
        .then((data) => {
          if (data.sections) {
            sections.forEach((s) => {
              const el = document.getElementById(s.id);
              if (!el) return;
              const target = el.querySelector(s.selector) || el;
              const parsed = new DOMParser().parseFromString(data.sections[s.section], 'text/html');
              const inner = parsed.querySelector(s.selector);
              if (inner) target.innerHTML = inner.innerHTML;
            });
          }
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) cartDrawer.open();
        })
        .finally(() => {
          this._adding = false;
        });
    }

    addMultipleToCart(items) {
      if (this._adding || !items.length) return Promise.reject();
      this._adding = true;
      const sections = this._getSectionsToRender();

      const addOne = (index) => {
        const item = items[index];
        const body = { id: item.variantId, quantity: 1, sections: index === items.length - 1 ? sections.map((s) => s.section) : undefined };
        if (item.sellingPlanId) body.selling_plan = item.sellingPlanId;
        if (item.properties) body.properties = item.properties;
        return fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.description || data.message) {
              return Promise.reject(new Error(data.description || data.message));
            }
            if (index < items.length - 1) return addOne(index + 1);
            if (data.sections) {
              sections.forEach((s) => {
                const el = document.getElementById(s.id);
                if (!el) return;
                const target = el.querySelector(s.selector) || el;
                const parsed = new DOMParser().parseFromString(data.sections[s.section], 'text/html');
                const inner = parsed.querySelector(s.selector);
                if (inner) target.innerHTML = inner.innerHTML;
              });
            }
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) cartDrawer.open();
          });
      };

      return addOne(0).finally(() => {
        this._adding = false;
      });
    }

    _setLoading(btn, loading) {
      if (!btn) return;
      btn.disabled = loading;
      btn.classList.toggle('loading', loading);
    }

    _onBundleOptionClick(e) {
      const option = e.currentTarget;
      if (option && option.classList.contains('js-bundle-option')) {
        this._setSelected(option);
        this._accordion(option);
        this._switchProductImages(option);
        this._switchFeatureImage(option);
        this._updateTabs(option);
      }
    }

    _updateTabs(option) {
      const variantId = option.dataset.variantId;
      if (variantId) {
        const htmlEls = this.querySelectorAll(`.product-bundle-form__tab-html[data-variant-id="${variantId}"]`);
        console.log(htmlEls)
        htmlEls.forEach(html => {
          const tab = html.dataset.tab;
          document.querySelector(`.product-tabs__content[data-tab="${tab}"]`).innerHTML = html.innerHTML;
        })
      }
    }

    _accordion(activeOption) {
      const allContents = this.querySelectorAll('.product-bundle-form__content');
      allContents.forEach((desc) => {
        const parent = desc.closest('.js-bundle-option');
        if (parent === activeOption) {
          desc.style.height = desc.scrollHeight + 'px';
        } else {
          desc.style.height = null;
        }
      });
    }

    _switchProductImages(optionEl) {
      const productSection = this.closest('.shopify-section');
      const imageSet = optionEl && optionEl.dataset.imageSet;

      if (!imageSet) return;
      const container = productSection.querySelector(`#${imageSet}-images`);
      if (!container) return;
      const subImages = container.querySelectorAll('.product__option-media-item');
      if (!subImages.length) return;
      const thumbs = productSection.querySelector('.product__media-thumbnails');
      const imagesWrap = productSection.querySelector('.product__media-list .swiper-wrapper');
      if (!thumbs || !imagesWrap) return;
      if (typeof productSlider !== 'undefined' && productSlider && typeof productSlider.destroy === 'function') {
        productSlider.destroy();
      }
      let newThumbs = '';
      let newImages = '';
      subImages.forEach((img, i) => {
        const thumbImageUrl = img.dataset.mobileUrl || img.dataset.url;
        const imageUrl = img.dataset.url || img.dataset.mobileUrl;
        const activeClass = i === 0 ? ' active' : '';
        newThumbs += `<li>
          <button class="product__media-thumbnail-item${activeClass}" data-media-id="${imageSet}-${i}" data-index="${i}">
            <div class="aspect-ratio img-fill" style="max-width: 100%; width: 100%;">
              <img data-src="${thumbImageUrl}" data-widths="[200,300,400,600,800,900,1000,1200]" data-sizes="auto" alt="${imageSet}" data-media-id="${i}" sizes="48px" src="${thumbImageUrl}">
            </div>
          </button>
        </li>`;
        newImages += `<li class="product__media-item swiper-slide" data-media-id="${imageSet}-main-${i}" role="group">
          <div class="product__media">
            <img src="${imageUrl}" alt="${imageSet}" class="img-fill">
            ${i == 0 ? img.innerHTML : ''}
          </div>
        </li>`;
      });
      thumbs.innerHTML = newThumbs;
      imagesWrap.innerHTML = newImages;
      if (typeof initSlider === 'function') initSlider();
    }

    _switchFeatureImage(optionEl) {
      const productFeature = this.closest('.product-feature');
      if (!productFeature || !optionEl) return;

      const desktopImage = productFeature.querySelector('[data-product-feature-image-desktop]');
      const mobileImage = productFeature.querySelector('[data-product-feature-image-mobile]');

      if (desktopImage && optionEl.dataset.featureImageDesktopSrc) {
        desktopImage.src = optionEl.dataset.featureImageDesktopSrc;
        desktopImage.srcset = optionEl.dataset.featureImageDesktopSrcset || optionEl.dataset.featureImageDesktopSrc;
      }

      if (mobileImage && optionEl.dataset.featureImageMobileSrc) {
        mobileImage.src = optionEl.dataset.featureImageMobileSrc;
        mobileImage.srcset = optionEl.dataset.featureImageMobileSrcset || optionEl.dataset.featureImageMobileSrc;
      }
    }

    _onAddSubscriptionClick() {
      const option = this._getSelectedOption();
      if (!option) return;
      const items = [{ variantId: option.dataset.variantId, sellingPlanId: option.dataset.sellingPlanId || null }];

      if (this.travelCheckbox && this.travelCheckbox.checked && this.travelCheckbox.dataset.variantId) {
        items.push({
          variantId: this.travelCheckbox.dataset.variantId,
          sellingPlanId: null,
        });
      }

      this._setLoading(this.addBtn, true);
      this.addMultipleToCart(items)
        .catch(() => {})
        .finally(() => this._setLoading(this.addBtn, false));
    }

    openModal() {
      if (!this.modal) return;
      const selected = this._getSelectedOption();
      const cards = this.modal.querySelectorAll('.js-bundle-modal-card');
      cards.forEach((c) => c.classList.remove('selected'));
      if (selected) {
        const vid = selected.dataset.variantId;
        const card = Array.from(cards).find((c) => c.dataset.variantId === vid);
        if (card) {
          card.classList.add('selected');
          this._updateModalFooter(card);
        } else if (cards.length) {
          cards[0].classList.add('selected');
          this._updateModalFooter(cards[0]);
        }
      } else if (cards.length) {
        cards[0].classList.add('selected');
        this._updateModalFooter(cards[0]);
      }
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const firstFocus = this.modal.querySelector('.js-bundle-modal-buy-onetime');
      if (firstFocus) firstFocus.focus();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.onetimeTrigger) this.onetimeTrigger.focus();
    }

    _handleModalKeydown(e) {
      if (e.key === 'Escape' && this.modal.getAttribute('aria-hidden') === 'false') {
        this.closeModal();
      }
    }

    _onOnetimeClick() {
      if (this.modal) this.openModal();
    }

    _onModalCardClick(e) {
      const card = e.currentTarget;
      if (!card.classList.contains('js-bundle-modal-card')) return;
      this.modal.querySelectorAll('.js-bundle-modal-card').forEach((c) => c.classList.remove('selected'));
      card.classList.add('selected');
      this._updateModalFooter(card);
    }

    _updateModalFooter(card) {
      if (!card) return;
      const priceEl = this.modal.querySelector('.js-bundle-modal-onetime-price');
      const subPriceEl = this.modal.querySelector('.js-bundle-modal-sub-price');
      const subscriptionVariantId = card.dataset.subscriptionVariantId || card.dataset.variantId;
      const oneTimeVariantId = card.dataset.oneTimeVariantId || subscriptionVariantId;
      if (priceEl && card.dataset.oneTimePriceFormatted) {
        // Intelligems: homepage buy-box popup reflects the IG test price for the selected option.
        const igOne = this._igHome ? this._igPrice(oneTimeVariantId) : null;
        priceEl.textContent = igOne || card.dataset.oneTimePriceFormatted;
      }
      if (subPriceEl && card.dataset.subscriptionPriceFormatted) {
        const igSub = this._igHome ? this._igPrice(subscriptionVariantId) : null;
        subPriceEl.textContent = igSub || card.dataset.subscriptionPriceFormatted;
      }
      const buyBtn = this.modal.querySelector('.js-bundle-modal-buy-onetime');
      const subBtn = this.modal.querySelector('.js-bundle-modal-add-subscription');
      if (buyBtn) {
        buyBtn.dataset.variantId = oneTimeVariantId;
        buyBtn.dataset.oneTimePrice = card.dataset.oneTimePrice;
      }
      if (subBtn) {
        subBtn.dataset.variantId = subscriptionVariantId;
        subBtn.dataset.sellingPlanId = card.dataset.sellingPlanId || '';
        subBtn.dataset.subscriptionPrice = card.dataset.subscriptionPrice;
      }
    }

    _onModalBuyOnetimeClick() {
      const btn = this.modal.querySelector('.js-bundle-modal-buy-onetime');
      const vid = btn.dataset.variantId;
      if (!vid) return;
      this._setLoading(btn, true);
      this.addToCart(vid, null)
        .catch(() => {})
        .finally(() => {
          this._setLoading(btn, false);
          this.closeModal();
        });
    }

    _onModalAddSubscriptionClick() {
      const btn = this.modal.querySelector('.js-bundle-modal-add-subscription');
      const vid = btn.dataset.variantId;
      const plan = btn.dataset.sellingPlanId || null;
      this._setLoading(btn, true);
      this.addToCart(vid, plan)
        .catch(() => {})
        .finally(() => {
          this._setLoading(btn, false);
          this.closeModal();
        });
    }
  }

  customElements.define('product-bundle-form', ProductBundleForm);
})();
